import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Loader2, Gift, Leaf } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { money, postCheckout, img } from "@/lib/api";

const GIFT_WRAP_PRICE = 5.0;

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const [loading, setLoading] = useState(false);
  const [isGift, setIsGift] = useState(false);
  const [giftWrap, setGiftWrap] = useState(false);
  const [giftNote, setGiftNote] = useState("");
  const [email, setEmail] = useState("");

  const total = subtotal + (isGift && giftWrap ? GIFT_WRAP_PRICE : 0);

  const checkout = async () => {
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) =>
          i.type === "herb"
            ? { herb_id: i.herbId, grams: i.grams, quantity: i.qty }
            : { catalog_object_id: i.variationId, quantity: i.qty }
        ),
        redirect_url: `${window.location.origin}/order-complete`,
        gift_wrap: isGift && giftWrap,
        gift_note: isGift && giftNote ? giftNote : null,
        buyer_email: email || null,
      };
      const res = await postCheckout(payload);
      window.location.assign(res.url);
    } catch (e) {
      toast.error("We couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 bg-wood-900/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setOpen(false)}
          data-testid="cart-overlay"
        >
          <motion.aside
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-cream"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: [0.16, 1, 0.3, 1], duration: 0.45 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-wood-900/10 px-6 py-5">
              <h2 className="font-serif text-2xl text-wood-900">Your Cart ({count})</h2>
              <button onClick={() => setOpen(false)} data-testid="cart-close" aria-label="Close cart">
                <X size={22} className="text-wood-900" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <p className="font-serif text-2xl text-wood-900">Your cart is quiet.</p>
                  <p className="mt-2 text-base text-wood-500">Add a little something warm.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-5">
                  {items.map((i) => (
                    <div key={i.variationId} className="flex gap-4" data-testid={`cart-item-${i.slug}`}>
                      {i.image ? (
                        <img src={img(i.image, 200)} alt={i.name} className="h-24 w-20 rounded-lg object-cover" />
                      ) : (
                        <div className="flex h-24 w-20 flex-col items-center justify-center rounded-lg bg-sage-pale text-center text-[9px] uppercase tracking-wider text-wood-500">
                          <Leaf size={18} className="mb-1 text-sage" />
                          Photo soon
                        </div>
                      )}
                      <div className="flex flex-1 flex-col">
                        <span className="font-serif text-lg leading-tight text-wood-900">{i.name}</span>
                        {i.variationName && <span className="text-sm text-wood-500">{i.variationName}</span>}
                        <span className="mt-1 text-base font-medium text-wood-700">{money(i.price)}</span>
                        <div className="mt-auto flex items-center gap-3">
                          <div className="flex items-center rounded-full border border-wood-900/20">
                            <button onClick={() => updateQty(i.variationId, i.qty - 1)} className="px-2.5 py-1.5" aria-label="Decrease">
                              <Minus size={15} />
                            </button>
                            <span className="min-w-6 text-center text-base">{i.qty}</span>
                            <button onClick={() => updateQty(i.variationId, i.qty + 1)} className="px-2.5 py-1.5" aria-label="Increase">
                              <Plus size={15} />
                            </button>
                          </div>
                          <button onClick={() => removeItem(i.variationId)} className="text-sm text-wood-500 underline">
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Gift options */}
                  <div className="mt-2 rounded-2xl border border-wood-900/10 bg-white p-4">
                    <button
                      onClick={() => setIsGift(!isGift)}
                      aria-expanded={isGift}
                      data-testid="gift-toggle"
                      className="flex w-full items-center justify-between text-left"
                    >
                      <span className="inline-flex items-center gap-2 font-serif text-lg text-wood-900">
                        <Gift size={18} className="text-honey-dark" /> This is a gift
                      </span>
                      <span className={`h-6 w-11 rounded-full p-0.5 transition-colors ${isGift ? "bg-honey" : "bg-wood-900/20"}`}>
                        <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${isGift ? "translate-x-5" : ""}`} />
                      </span>
                    </button>
                    <AnimatePresence>
                      {isGift && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                          <label className="mt-4 flex items-start gap-3 text-base text-wood-700">
                            <input type="checkbox" checked={giftWrap} onChange={(e) => setGiftWrap(e.target.checked)} data-testid="gift-wrap-checkbox" className="mt-1 h-5 w-5 accent-honey-dark" />
                            Add gift wrapping (+{money(GIFT_WRAP_PRICE)})
                          </label>
                          <textarea
                            value={giftNote}
                            onChange={(e) => setGiftNote(e.target.value.slice(0, 500))}
                            placeholder="Add a gift note (optional)…"
                            data-testid="gift-note"
                            rows={3}
                            className="mt-3 w-full resize-none rounded-xl border border-wood-900/20 bg-cream px-4 py-3 text-base text-wood-900 outline-none focus:border-honey-dark"
                          />
                          <span className="mt-1 block text-xs text-wood-500">{giftNote.length}/500 · Your note is sent with the order.</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-wood-900/10 px-6 py-5">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email for order updates (optional)"
                  data-testid="cart-email"
                  className="mb-4 w-full rounded-full border border-wood-900/20 bg-white px-5 py-3 text-base text-wood-900 outline-none focus:border-honey-dark"
                />
                {isGift && giftWrap && (
                  <div className="flex items-center justify-between pb-1 text-base text-wood-700">
                    <span>Gift wrapping</span><span>{money(GIFT_WRAP_PRICE)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pb-4">
                  <span className="text-base text-wood-500">Subtotal</span>
                  <span className="font-serif text-2xl text-wood-900" data-testid="cart-subtotal">{money(total)}</span>
                </div>
                <p className="pb-4 text-sm text-wood-500">Taxes & shipping calculated securely at Square checkout.</p>
                <button
                  onClick={checkout}
                  disabled={loading}
                  data-testid="checkout-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-wood-900 py-4 text-lg font-medium tracking-wide text-cream transition-colors hover:bg-honey-dark disabled:opacity-60"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : "Secure Checkout with Square"}
                </button>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
